import React, { useState } from 'react';
import moment from 'moment';
import Select from 'react-select';
import './superadmin-dashboard.css';

const SuperAdminDashboardView = ({
  dashboardData,
  onFilterChange,
  onBack,
  onRefresh,
  queryParams,
}) => {
  const [filters, setFilters] = useState({
    startDate: queryParams.startDate || '',
    endDate: queryParams.endDate || '',
    client_id: queryParams.client_id || '',
    employee_id: queryParams.employee_id || '',
    status_filter: queryParams.status_filter || '',
    page: queryParams.page || 1,
    limit: queryParams.limit || 20,
    include_details: queryParams.include_details || 'false',
  });

  const [activeView, setActiveView] = useState('overview');

  // Handle filter updates
  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Format date for display
  const formatDate = (date) => {
    return moment(date).format('MMM DD, YYYY HH:mm');
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusColors = {
      PENDING: '#ffa500',
      IN_PROGRESS: '#2196f3',
      COMPLETED: '#4caf50',
      ON_HOLD: '#ff5722',
      CANCELLED: '#f44336',
    };

    const color = statusColors[status?.toUpperCase()] || '#757575';

    return (
      <span className='status-badge' style={{ backgroundColor: color }}>
        {status}
      </span>
    );
  };

  // Client options for filter
  const clientOptions =
    dashboardData?.filters?.available?.clients?.map((client) => ({
      value: client._id,
      label: client.name,
    })) || [];

  // Employee options for filter
  const employeeOptions =
    dashboardData?.filters?.available?.employees?.map((emp) => ({
      value: emp._id,
      label: emp.name,
    })) || [];

  // Status options for filter
  const statusOptions =
    dashboardData?.filters?.available?.statuses?.map((status) => ({
      value: status._id,
      label: status.name,
    })) || [];

  return (
    <div className='superadmin-dashboard-container'>
      {/* Header Section */}
      <div className='dashboard-header'>
        <div className='header-left'>
          <button className='back-btn' onClick={onBack}>
            <i className='fa fa-arrow-left'></i>
            Back to Dashboard
          </button>
          <div className='dashboard-info'>
            <h1 className='dashboard-title'>Super Admin Dashboard</h1>
            <div className='dashboard-meta'>
              <span>
                <i className='fa fa-calendar'></i>
                {dashboardData?.metadata?.date_range || 'All time'}
              </span>
              <span>
                <i className='fa fa-users'></i>
                {dashboardData?.overview?.total_clients || 0} Active Clients
              </span>
              <span>
                <i className='fa fa-user-tie'></i>
                {dashboardData?.overview?.active_employees || 0} Team Members
              </span>
            </div>
          </div>
        </div>
        <div className='header-right'>
          <button className='refresh-btn' onClick={onRefresh}>
            <i className='fa fa-refresh'></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className='dashboard-filters'>
        <div className='filters-grid'>
          <div className='filter-group'>
            <label>Start Date</label>
            <input
              type='date'
              value={filters.startDate}
              onChange={(e) => updateFilter('startDate', e.target.value)}
              className='filter-input'
            />
          </div>

          <div className='filter-group'>
            <label>End Date</label>
            <input
              type='date'
              value={filters.endDate}
              onChange={(e) => updateFilter('endDate', e.target.value)}
              className='filter-input'
            />
          </div>

          <div className='filter-group'>
            <label>Client</label>
            <Select
              options={clientOptions}
              value={clientOptions.find(
                (opt) => opt.value === filters.client_id
              )}
              onChange={(option) =>
                updateFilter('client_id', option?.value || '')
              }
              placeholder='Select Client'
              isClearable
              className='filter-select'
            />
          </div>

          <div className='filter-group'>
            <label>Employee</label>
            <Select
              options={employeeOptions}
              value={employeeOptions.find(
                (opt) => opt.value === filters.employee_id
              )}
              onChange={(option) =>
                updateFilter('employee_id', option?.value || '')
              }
              placeholder='Select Employee'
              isClearable
              className='filter-select'
            />
          </div>

          <div className='filter-group'>
            <label>Status</label>
            <Select
              options={statusOptions}
              value={statusOptions.find(
                (opt) => opt.value === filters.status_filter
              )}
              onChange={(option) =>
                updateFilter('status_filter', option?.value || '')
              }
              placeholder='Select Status'
              isClearable
              className='filter-select'
            />
          </div>
        </div>

        <div className='view-toggles'>
          <label className='toggle-label'>
            <input
              type='checkbox'
              checked={filters.include_details === 'true'}
              onChange={(e) =>
                updateFilter(
                  'include_details',
                  e.target.checked ? 'true' : 'false'
                )
              }
            />
            Include Detailed Analytics
          </label>
        </div>
      </div>

      {/* Overview Cards */}
      <div className='overview-cards'>
        <div className='overview-card'>
          <div className='card-icon'>
            <i className='fa fa-building'></i>
          </div>
          <div className='card-content'>
            <h3>{dashboardData?.overview?.total_clients || 0}</h3>
            <p>Total Clients</p>
          </div>
        </div>

        <div className='overview-card'>
          <div className='card-icon'>
            <i className='fa fa-tasks'></i>
          </div>
          <div className='card-content'>
            <h3>{dashboardData?.overview?.total_activities || 0}</h3>
            <p>Total Activities</p>
          </div>
        </div>

        <div className='overview-card completed'>
          <div className='card-icon'>
            <i className='fa fa-check-circle'></i>
          </div>
          <div className='card-content'>
            <h3>{dashboardData?.overview?.total_completed || 0}</h3>
            <p>Completed</p>
          </div>
        </div>

        <div className='overview-card overdue'>
          <div className='card-icon'>
            <i className='fa fa-exclamation-triangle'></i>
          </div>
          <div className='card-content'>
            <h3>{dashboardData?.overview?.total_overdue || 0}</h3>
            <p>Overdue</p>
          </div>
        </div>

        <div className='overview-card completion-rate'>
          <div className='card-icon'>
            <i className='fa fa-chart-pie'></i>
          </div>
          <div className='card-content'>
            <h3>{dashboardData?.overview?.overall_completion_rate || 0}%</h3>
            <p>Overall Completion Rate</p>
          </div>
        </div>

        <div className='overview-card employees'>
          <div className='card-icon'>
            <i className='fa fa-users'></i>
          </div>
          <div className='card-content'>
            <h3>{dashboardData?.overview?.active_employees || 0}</h3>
            <p>Active Employees</p>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className='view-tabs'>
        <button
          className={`tab-btn ${activeView === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeView === 'employees' ? 'active' : ''}`}
          onClick={() => setActiveView('employees')}
        >
          Employee Performance
        </button>
        <button
          className={`tab-btn ${activeView === 'clients' ? 'active' : ''}`}
          onClick={() => setActiveView('clients')}
        >
          Top Clients
        </button>
        <button
          className={`tab-btn ${activeView === 'status' ? 'active' : ''}`}
          onClick={() => setActiveView('status')}
        >
          Status Analysis
        </button>
      </div>

      {/* Content Based on Active View */}
      <div className='dashboard-content'>
        {activeView === 'overview' && (
          <div className='overview-view'>
            {/* Status Breakdown */}
            {dashboardData?.status_breakdown && (
              <div className='status-breakdown'>
                <h3>Status Distribution</h3>
                <div className='status-grid'>
                  {dashboardData.status_breakdown.map((status, index) => (
                    <div key={index} className='status-item'>
                      <div
                        className='status-count'
                        style={{ color: status.color }}
                      >
                        {status.count}
                      </div>
                      <div className='status-label'>
                        {getStatusBadge(status.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeView === 'employees' && (
          <div className='employees-view'>
            {dashboardData?.employee_workload &&
            dashboardData.employee_workload.length > 0 ? (
              <div className='employees-table'>
                <h3>Employee Performance Overview</h3>
                <table className='table'>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Total Tasks</th>
                      <th>Clients</th>
                      <th>Completed</th>
                      <th>Completion Rate</th>
                      <th>Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.employee_workload.map((employee, index) => (
                      <tr key={index}>
                        <td>
                          <div className='employee-info'>
                            <strong>{employee.employee?.name}</strong>
                            <div className='employee-email'>
                              {employee.employee?.email}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className='task-count'>
                            {employee.total_tasks}
                          </span>
                        </td>
                        <td>
                          <span className='client-count'>
                            {employee.client_count}
                          </span>
                        </td>
                        <td>
                          <span className='completed-count'>
                            {employee.completed_tasks}
                          </span>
                        </td>
                        <td>
                          <div className='completion-rate'>
                            <span>{employee.completion_rate}%</span>
                            <div className='progress-bar'>
                              <div
                                className='progress-fill'
                                style={{
                                  width: `${employee.completion_rate}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='performance-indicator'>
                            {employee.completion_rate >= 80 ? (
                              <span className='performance-excellent'>
                                <i className='fa fa-star'></i> Excellent
                              </span>
                            ) : employee.completion_rate >= 60 ? (
                              <span className='performance-good'>
                                <i className='fa fa-thumbs-up'></i> Good
                              </span>
                            ) : (
                              <span className='performance-needs-improvement'>
                                <i className='fa fa-exclamation-circle'></i>{' '}
                                Needs Improvement
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {dashboardData?.pagination && (
                  <div className='pagination-info'>
                    <span>
                      Showing {dashboardData.employee_workload.length} of{' '}
                      {dashboardData.pagination.total_employees} employees
                    </span>
                    {dashboardData.pagination.has_more_data && (
                      <button
                        className='load-more-btn'
                        onClick={() =>
                          updateFilter('page', parseInt(filters.page) + 1)
                        }
                      >
                        Load More
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className='empty-state'>
                <i className='fa fa-users'></i>
                <h3>No Employee Data</h3>
                <p>
                  No employee performance data found for the selected filters.
                </p>
              </div>
            )}
          </div>
        )}

        {activeView === 'clients' && (
          <div className='clients-view'>
            {dashboardData?.top_clients &&
            dashboardData.top_clients.length > 0 ? (
              <div className='clients-table'>
                <h3>Top Clients by Activity</h3>
                <table className='table'>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Client Name</th>
                      <th>Total Activities</th>
                      <th>Completed</th>
                      <th>Completion Rate</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.top_clients.map((client, index) => (
                      <tr key={index}>
                        <td>
                          <div className='rank-badge'>#{index + 1}</div>
                        </td>
                        <td>
                          <strong>{client.client_name}</strong>
                        </td>
                        <td>{client.total_activities}</td>
                        <td>{client.completed}</td>
                        <td>
                          <div className='completion-rate'>
                            <span>{client.completion_rate}%</span>
                            <div className='progress-bar'>
                              <div
                                className='progress-fill'
                                style={{ width: `${client.completion_rate}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {client.completion_rate >= 80 ? (
                            <span className='client-status excellent'>
                              Excellent
                            </span>
                          ) : client.completion_rate >= 60 ? (
                            <span className='client-status good'>Good</span>
                          ) : (
                            <span className='client-status attention'>
                              Needs Attention
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='empty-state'>
                <i className='fa fa-building'></i>
                <h3>No Client Data</h3>
                <p>
                  Enable "Include Detailed Analytics" to see top client
                  performance.
                </p>
              </div>
            )}
          </div>
        )}

        {activeView === 'status' && (
          <div className='status-view'>
            {dashboardData?.status_breakdown &&
            dashboardData.status_breakdown.length > 0 ? (
              <div className='status-analysis'>
                <h3>Status Analysis</h3>
                <div className='status-cards'>
                  {dashboardData.status_breakdown.map((status, index) => (
                    <div key={index} className='status-analysis-card'>
                      <div className='status-header'>
                        <h4>{status.status}</h4>
                        <div className='status-count-large'>{status.count}</div>
                      </div>
                      <div className='status-details'>
                        <div className='status-percentage'>
                          {(
                            (status.count /
                              dashboardData.overview.total_activities) *
                            100
                          ).toFixed(1)}
                          %<span>of total</span>
                        </div>
                        <div
                          className='status-bar'
                          style={{
                            backgroundColor: status.color,
                            width: `${
                              (status.count /
                                Math.max(
                                  ...dashboardData.status_breakdown.map(
                                    (s) => s.count
                                  )
                                )) *
                              100
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className='empty-state'>
                <i className='fa fa-chart-bar'></i>
                <h3>No Status Data</h3>
                <p>
                  No status breakdown data available for the selected filters.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Metadata */}
      {dashboardData?.metadata && (
        <div className='dashboard-metadata'>
          <small>
            Report: {dashboardData.metadata.report_type} | Generated at:{' '}
            {formatDate(dashboardData.metadata.generated_at)} | Performance tip:{' '}
            {dashboardData.metadata.performance_tip}
          </small>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboardView;
